import { Controller, type Control, type FieldPath, type FieldValues } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import FormField from '../FormField'
import TextInput from '../TextInput'
import { resolveFieldError } from '../../../lib/forms/messages'

/**
 * RHF-bağlı metin alanı (020). `Controller` + paylaşılan `TextInput` + `FormField` (label/zorunlu/
 * alan-altı hata). Değer/hata/zorunluluk RHF'den; hata metni i18n ile çözülür. Reuse Mandate.
 */
interface FormTextFieldProps<T extends FieldValues> {
  control: Control<T>
  name: FieldPath<T>
  label: string
  required?: boolean
  full?: boolean
  placeholder?: string
  maxLength?: number
  inputMode?: 'text' | 'numeric'
  autoFocus?: boolean
  /**
   * Alanı devre dışı bırakır (059: şablon formunda alıcı alanları yalnız "Eposta" türünde etkindir).
   * Değer RHF'de kalır; temizleme sorumluluğu çağırandadır.
   */
  disabled?: boolean
  /**
   * <b>Salt okunur</b> — `disabled`'dan FARKLI ve görüntüleme kipinde doğru olan budur:
   * denetim odaklanılabilir ve metni seçilebilir kalır, yalnız değiştirilemez. `disabled`
   * kontrolü klavye odak sırasından tamamen çıkarır, yani taşan metin okunamaz hâle gelir
   * (THY AI review HIGH bulgusu, 26.10.2026).
   */
  readOnly?: boolean
}

export default function FormTextField<T extends FieldValues>({
  control,
  name,
  label,
  required,
  full,
  placeholder,
  maxLength,
  inputMode,
  autoFocus,
  disabled,
  readOnly,
}: Readonly<FormTextFieldProps<T>>) {
  const { t } = useTranslation()
  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <FormField
          label={label}
          htmlFor={name}
          required={required}
          full={full}
          error={resolveFieldError(fieldState.error?.message, t)}
        >
          <TextInput
            id={name}
            name={field.name}
            value={field.value ?? ''}
            onChange={field.onChange}
            onBlur={field.onBlur}
            placeholder={placeholder}
            maxLength={maxLength}
            inputMode={inputMode}
            autoFocus={autoFocus}
            disabled={disabled}
            readOnly={readOnly}
            className={fieldState.error ? 'p-invalid' : undefined}
          />
        </FormField>
      )}
    />
  )
}
